import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import {
  HttpError,
  SupplierContractCheckRequest,
  SupplierContractCheckResponse,
} from '../../../core/clients/omni-core/models';
import { Api } from '../../../core/clients/omni-core/api';
import { OrganizationsStore } from '../../organizations/store/organizations.store';
import {
  createSupplierContractCheck,
  deleteSupplierContractCheck,
  getSupplierContractCheck,
  listSupplierContractChecks,
  reopenSupplierContractCheck,
  submitSupplierContractCheck,
  updateSupplierContractCheck,
} from '../../../core/clients/omni-core/functions';

export type SupplierContractCheckData = SupplierContractCheckResponse;

export interface SupplierContractCheckState {
  id: string;

  data: SupplierContractCheckData | null;

  isLoading: boolean;
  error: HttpError | unknown | null;

  isUpdateLoading: boolean;
  updateError: HttpError | unknown | null;

  isDeleteLoading: boolean;
  deleteError: HttpError | unknown | null;
  isDeleted: boolean;

  isPublishLoading: boolean;
  publishError: HttpError | unknown | null;
}

@Injectable()
export class SupplierContractChecksStore {
  private api = inject(Api);
  private organizationsStore = inject(OrganizationsStore);

  // Global states
  #isLoading = signal<boolean>(false);
  #error = signal<HttpError | unknown | null>(null);
  #allLoaded = signal<boolean>(false);

  // Data
  #supplierContractChecks = signal<SupplierContractCheckState[] | null>(null);

  // Create dialog
  #isCreateLoading = signal<boolean>(false);
  #createError = signal<HttpError | unknown | null>(null);

  // Public computed signals
  isLoading = computed(() => this.#isLoading());
  error = computed(() => this.#error());
  allLoaded = computed(() => this.#allLoaded());
  supplierContractChecks = computed(() => this.#supplierContractChecks());

  isCreateLoading = computed(() => this.#isCreateLoading());
  createError = computed(() => this.#createError());

  organizationId = computed(() => this.organizationsStore.selectedId());
  supplierContractId = signal<string | null>(null);

  filteredSupplierContractChecks = computed(() => {
    return (this.#supplierContractChecks() ?? [])
      .filter((check) => !!check.data && !check.isDeleted)
      .sort((a, b) => b.data!.metadata.createdAt.localeCompare(a.data!.metadata.createdAt));
  });

  private patchItem(checkId: string, patch: Partial<SupplierContractCheckState>): void {
    const check = this.#supplierContractChecks()?.find((check) => check.id === checkId);
    if (!check) {
      throw new Error('SupplierContractCheck not found in store');
    }
    this.#supplierContractChecks.update(
      (checks) =>
        checks?.map((check) => (check.id !== checkId ? check : { ...check, ...patch })) ?? null,
    );
  }

  private setItem(checkId: string, newItem: SupplierContractCheckState): void {
    const check = this.#supplierContractChecks()?.find((check) => check.id === checkId);
    if (!check) {
      this.#supplierContractChecks.update((checks) => [...(checks ?? []), newItem]);
      return;
    }
    this.#supplierContractChecks.update(
      (checks) => checks?.map((check) => (check.id !== checkId ? check : newItem)) ?? null,
    );
  }

  reset(): void {
    this.#isLoading.set(false);
    this.#error.set(null);
    this.#allLoaded.set(false);
    this.#supplierContractChecks.set(null);
    this.#isCreateLoading.set(false);
    this.#createError.set(null);
  }

  async load(): Promise<void> {
    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      this.#error.set(new Error('No organization selected'));
      this.#supplierContractChecks.set(null);
      this.#isLoading.set(false);
      return;
    }
    const supplierContractId = untracked(() => this.supplierContractId());
    if (!supplierContractId) {
      this.#error.set(new Error('No supplier-contract selected'));
      this.#supplierContractChecks.set(null);
      this.#isLoading.set(false);
      return;
    }
    this.#isLoading.set(true);
    this.#supplierContractChecks.set(null);
    this.#error.set(null);
    this.#allLoaded.set(false);
    try {
      const res = await this.api.invoke(listSupplierContractChecks, {
        oid: organizationId,
        supplierContractId,
        limit: 999,
      });
      this.#supplierContractChecks.set(
        res.items.map((data) => ({
          id: data.id,
          data: data,
          isLoading: false,
          error: null,
          isUpdateLoading: false,
          updateError: null,
          isDeleteLoading: false,
          deleteError: null,
          isDeleted: false,
          isPublishLoading: false,
          publishError: null,
        })),
      );
      this.#isLoading.set(false);
      this.#allLoaded.set(true);
    } catch (error) {
      this.#error.set(error);
      this.#isLoading.set(false);
    }
  }

  async loadItem(checkId: string): Promise<void> {
    this.setItem(checkId, {
      id: checkId,
      isLoading: true,
      error: null,
      data: null,
      isUpdateLoading: false,
      updateError: null,
      isDeleteLoading: false,
      deleteError: null,
      isDeleted: false,
      isPublishLoading: false,
      publishError: null,
    });

    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      this.patchItem(checkId, {
        error: new Error('No organization selected'),
        isLoading: false,
      });
      return;
    }
    try {
      const res = await this.api.invoke(getSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      this.patchItem(checkId, { data: res, isLoading: false });
    } catch (error) {
      this.patchItem(checkId, { error, isLoading: false });
    }
  }

  async create(): Promise<string | null> {
    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      const error = new Error('No organization selected');
      this.#createError.set(error);
      this.#isCreateLoading.set(false);
      throw error;
    }
    const supplierContractId = untracked(() => this.supplierContractId());
    if (!supplierContractId) {
      const error = new Error('No supplier-contract selected');
      this.#error.set(error);
      this.#supplierContractChecks.set(null);
      this.#isLoading.set(false);
      throw error;
    }
    this.#isCreateLoading.set(true);
    this.#createError.set(null);
    try {
      const createRes = await this.api.invoke(createSupplierContractCheck, {
        oid: organizationId,
        body: {
          supplierContractIds: [supplierContractId],
          CHECK_CONSUMPTION_FLEXIBILITY: {
            result: 'NOT_SET',
          },
          CHECK_CONTRACT_FLEXIBILITY: {
            result: 'NOT_SET',
          },
          CHECK_DECISION_MAKING_EFFORT: {
            result: 'NOT_SET',
          },
          CHECK_FIX_COST: {
            result: 'NOT_SET',
          },
          CHECK_FIX_PRICE_GUARANTEE: {
            result: 'NOT_SET',
          },
          CHECK_INDEX_PRICE_DEPENDENCY: {
            result: 'NOT_SET',
          },
          CHECK_MARGIN_TRANSPARENCY: {
            result: 'NOT_SET',
          },
          CHECK_OVERALL_COST: {
            result: 'NOT_SET',
          },
          CHECK_PRICE_CONSUMPTION_MATCH: {
            result: 'NOT_SET',
          },
          CHECK_PRICING_BILLING_COMPLEXITY: {
            result: 'NOT_SET',
          },
          CHECK_TRANCHE_TIMING_PERFORMANCE: {
            result: 'NOT_SET',
          },
        },
      });
      const res = await this.api.invoke(getSupplierContractCheck, {
        oid: organizationId,
        id: createRes.id,
      });
      this.#supplierContractChecks.update((checks) => [
        ...(checks ?? []),
        {
          id: res.id,
          data: res,
          isLoading: false,
          error: null,
          isUpdateLoading: false,
          updateError: null,
          isDeleteLoading: false,
          deleteError: null,
          isDeleted: false,
          isPublishLoading: false,
          publishError: null,
        },
      ]);
      this.#isCreateLoading.set(false);
      return createRes.id;
    } catch (error) {
      this.#createError.set(error);
      this.#isCreateLoading.set(false);
      return null;
    }
  }

  async update(checkId: string, payload: SupplierContractCheckRequest): Promise<void> {
    const check = this.#supplierContractChecks()?.find((ei) => ei.id === checkId);
    if (!check) {
      throw new Error('Check not found in store');
    }
    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      const error = new Error('No organization selected');
      this.patchItem(checkId, { updateError: error });
      throw error;
    }
    this.patchItem(checkId, {
      isUpdateLoading: true,
      updateError: null,
    });
    try {
      await this.api.invoke(updateSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
        body: payload,
      });
      const res = await this.api.invoke(getSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      this.patchItem(checkId, { data: res, isUpdateLoading: false });
    } catch (error) {
      this.patchItem(checkId, {
        updateError: error,
        isUpdateLoading: false,
      });
      throw error;
    }
  }

  async delete(checkId: string): Promise<void> {
    const check = this.#supplierContractChecks()?.find((ei) => ei.id === checkId);
    if (!check) {
      throw new Error('Check not found in store');
    }
    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      const error = new Error('No organization selected');
      this.patchItem(checkId, { deleteError: error });
      throw error;
    }
    this.patchItem(checkId, { isDeleteLoading: true, deleteError: null });
    try {
      await this.api.invoke(deleteSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      this.patchItem(checkId, { isDeleted: true, isDeleteLoading: false });
    } catch (error) {
      this.patchItem(checkId, { deleteError: error, isDeleteLoading: false });
      throw error;
    }
  }

  async publish(checkId: string): Promise<void> {
    const check = this.#supplierContractChecks()?.find((ei) => ei.id === checkId);
    if (!check) {
      throw new Error('Check not found in store');
    }
    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      const error = new Error('No organization selected');
      this.patchItem(checkId, { updateError: error });
      throw error;
    }
    this.patchItem(checkId, {
      isPublishLoading: true,
      publishError: null,
    });
    try {
      await this.api.invoke(submitSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      const res = await this.api.invoke(getSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      this.patchItem(checkId, { data: res, isPublishLoading: false });
    } catch (error) {
      this.patchItem(checkId, {
        publishError: error,
        isPublishLoading: false,
      });
      throw error;
    }
  }

  async reopen(checkId: string): Promise<void> {
    const check = this.#supplierContractChecks()?.find((ei) => ei.id === checkId);
    if (!check) {
      throw new Error('Check not found in store');
    }
    const organizationId = untracked(() => this.organizationId());
    if (!organizationId) {
      const error = new Error('No organization selected');
      this.patchItem(checkId, { updateError: error });
      throw error;
    }
    this.patchItem(checkId, {
      isUpdateLoading: true,
      updateError: null,
    });
    try {
      await this.api.invoke(reopenSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      const res = await this.api.invoke(getSupplierContractCheck, {
        oid: organizationId,
        id: checkId,
      });
      this.patchItem(checkId, { data: res, isUpdateLoading: false });
    } catch (error) {
      this.patchItem(checkId, {
        updateError: error,
        isUpdateLoading: false,
      });
      throw error;
    }
  }
}
